import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, of, withLatestFrom } from 'rxjs';
import { DatepickerRange, KendoSortDescriptor, RangeDatepickerComponent } from '@ui/aos-lib';

import { CustomerDetailsEventsActions } from './customer-details-events.actions';
import { selectFiltersPayload, selectTableState, selectDetails } from './customer-details-events.reducer';
import { CustomersApiService } from '../../../api/customers-api.service';
import { SortMapping } from '../../../services/customer-details.service';
import { CustomerFieldName } from '../../../customers.constants';
import { Events } from '../../../../../network-monitoring/events-monitor/events-monitor.types';
import { DurationPipe } from 'src/app/shared/pipes/duration.pipe';
import { OrderDirection } from '../../../../../equipment/devices/components/devices.models';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { EventsFieldName } from '../customer-details-events.constants';
import { multiplierWithUom } from '../../../../../../shared/utils';

const durationPipe = new DurationPipe();

function makeSortFilter(sort: KendoSortDescriptor[]): {
  columnName: CustomerFieldName;
  orderDirection: OrderDirection;
} {
  return {
    columnName: sort[0].field as CustomerFieldName,
    orderDirection: sort[0].dir ? (sort[0].dir as string as OrderDirection) : OrderDirection.DESC
  };
}

export const getEvents$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    customersApiService = inject(CustomersApiService),
    timezoneService = inject(TimezoneService)
  ) =>
    actions$.pipe(
      ofType(CustomerDetailsEventsActions.getEvents),
      withLatestFrom(store.select(selectFiltersPayload), store.select(selectTableState), store.select(selectDetails)),
      exhaustMap(([, filters, tableState, details]) => {
        const earliestAvailableServiceStartDate = details?.devices
          ? details.devices.reduce<string | undefined>((earliest, device) => {
              if (device?.serviceStartDate) {
                const currentDate = new Date(device.serviceStartDate);
                const earliestDate = earliest ? new Date(earliest) : undefined;
                return !earliest || (earliestDate && currentDate < earliestDate) ? device.serviceStartDate : earliest;
              }
              return earliest;
            }, undefined)
          : undefined;
        const limitedRange = timezoneService.getShiftedDatepickerRangeStringified({
          start: new Date((RangeDatepickerComponent.LAST_YEAR.getValue() as DatepickerRange).start),
          end: new Date((RangeDatepickerComponent.LAST_YEAR.getValue() as DatepickerRange).end)
        });
        if (earliestAvailableServiceStartDate) {
          limitedRange[0] = new Date(earliestAvailableServiceStartDate).toISOString();
        }
        const sort = tableState.sort && makeSortFilter(tableState.sort);
        const payload = {
          ...tableState,
          filters: [...filters, { filterType: EventsFieldName.LastDetectedUtc, filteredValues: limitedRange }],
          sort: sort?.columnName
            ? {
                ...sort,
                columnName: SortMapping[
                  sort.columnName as unknown as keyof typeof SortMapping
                ] as unknown as EventsFieldName
              }
            : sort
        };

        return customersApiService.getDetailsEvents(payload).pipe(
          map(events => {
            const { items, totalCount } = events;
            const data: Events[] = items.map(event => {
              return {
                ...event,
                lastDetectedUtc: timezoneService.formatLong(event.lastDetectedUtc),
                startedAtUtc: timezoneService.formatLong(event.startedAtUtc),
                endedAtUtc: event.endedAtUtc ? timezoneService.formatLong(event.endedAtUtc) : null,
                duration: durationPipe.transform(event.lastDetectedUtc, event.endedAtUtc),
                statusText: event.status,
                unit: multiplierWithUom(event.multiplier, event.unitOfMeasure)
              };
            });
            return CustomerDetailsEventsActions.getEventsSuccess({ eventsData: { data, total: totalCount } });
          }),
          catchError(() => of(CustomerDetailsEventsActions.getEventsFailure({ error: 'Failed to load events' })))
        );
      })
    ),
  { functional: true }
);
